import { useEffect } from 'react'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { ensureValidSession } from '@/lib/auth-session'
import { syncAuthCacheScope } from '@/lib/auth-cache-scope'
import { AppShell } from '@/components/layout/app-shell'
import { CommandPalette } from '@/components/command-palette'
import { useTenantBranding } from '@/hooks/use-branding'
import { applyAccentTokens, applyFontPairing, DEFAULT_ACCENT, DEFAULT_FONT_PAIRING } from '@/lib/theme'

export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ location }) => {
    // A session the server has REJECTED never renders the CRM shell — but read
    // `ensureValidSession` before relying on that: it validates once per access
    // token and then reuses the memoized answer, so a session terminated
    // server-side mid-token is not seen here until the token changes.
    const session = await ensureValidSession()
    // The cache's identity boundary. Auth EVENTS already clear the query cache
    // (use-auth.ts), but a session can change with no event delivered to this
    // tab — a cross-tab re-login whose broadcast was lost, or a session that
    // is simply gone. Every authed navigation passes here, so a scope change
    // observed at this line clears cached tenant data before anything renders,
    // including on the redirect path below (client-side, tears nothing down).
    syncAuthCacheScope(session)
    if (!session) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: AuthedLayout,
})

function AuthedLayout() {
  const { data: branding } = useTenantBranding()

  useEffect(() => {
    function applyBranding() {
      const isDark = document.documentElement.classList.contains('dark')
      applyAccentTokens(branding?.accent ?? DEFAULT_ACCENT, isDark)
      applyFontPairing(branding?.font_pairing ?? DEFAULT_FONT_PAIRING)
    }

    applyBranding()

    // Re-apply whenever useTheme flips the dark/light class on <html>
    const observer = new MutationObserver(applyBranding)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [branding])

  return (
    <AppShell>
      <Outlet />
      <CommandPalette />
    </AppShell>
  )
}
